import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Mail, ShieldCheck, Zap, Heart, Bell, Loader2, ArrowLeft } from "lucide-react";
import Header from "@/components/public/Header";
import Footer from "@/components/public/Footer";
import { Logo } from "@/components/public/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { memberApi, setMemberToken } from "@/lib/member";

const BENEFITS = [
  { icon: Zap, title: "Fast, easy booking", desc: "Request tours and quotes in seconds and pick up right where you left off." },
  { icon: Heart, title: "Save your favourites", desc: "Bookmark the CoWorkZen locations and workspaces you love." },
  { icon: ShieldCheck, title: "One secure login", desc: "Passwordless email OTP — no passwords to remember or leak." },
  { icon: Bell, title: "Stay in the loop", desc: "Track your enquiries and get timely updates in one place." },
];

const SPACE_TYPES = ["Coworking", "BTS", "Managed Office", "Meeting Room", "Training Room"];
const CITIES = ["Noida", "Greater Noida", "Delhi", "Gurugram", "Ghaziabad", "Faridabad", "Other"];

export default function Account() {
  const navigate = useNavigate();
  const [step, setStep] = useState("email"); // email | details | otp
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [city, setCity] = useState("");
  const [spaceType, setSpaceType] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  // Step 1: email lookup → existing members get a login code; new members go to signup fields
  const continueEmail = async () => {
    if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
    setLoading(true);
    try {
      const { data } = await memberApi.checkEmail({ email });
      if (data.exists) {
        await memberApi.requestOtp({ email, mode: "login" });
        toast.success("Welcome back! We've emailed your login code");
        setStep("otp");
      } else {
        setStep("details");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Something went wrong"); }
    finally { setLoading(false); }
  };

  // Step 2 (new members): collect profile fields, then send the verification code
  const createAccount = async () => {
    if (!name.trim()) { toast.error("Enter your full name"); return; }
    if (!phone.trim()) { toast.error("Enter your phone number"); return; }
    setLoading(true);
    try {
      await memberApi.requestOtp({ email, name, phone, company, city, space_type: spaceType, mode: "signup" });
      toast.success("Verification code sent to your email");
      setStep("otp");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send code"); }
    finally { setLoading(false); }
  };

  const verify = async () => {
    if (code.length < 4) { toast.error("Enter the code"); return; }
    setLoading(true);
    try {
      const { data } = await memberApi.verifyOtp({ email, code, name });
      setMemberToken(data.token);
      toast.success(`Welcome, ${data.member.name}!`);
      navigate("/dashboard");
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
    finally { setLoading(false); }
  };

  const resend = () => memberApi.requestOtp({
    email, name, phone, company, city, space_type: spaceType, mode: step === "otp" ? "signup" : "login",
  }).then(() => toast.success("Code resent")).catch(() => toast.error("Could not resend"));

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <main className="flex-1 grid lg:grid-cols-[1fr_42%]">
        {/* Left */}
        <div className="flex flex-col">
          <div className="flex-1 flex items-center justify-center px-6 py-12 sm:py-16">
            <div className="w-full max-w-md" data-testid="account-panel">
              <div className="lg:hidden mb-8"><Logo /></div>
              <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-[#16161a]">Sign in or create an account</h1>
              <p className="text-slate-600 mt-3">Unlock a faster booking experience and manage everything about your CoWorkZen workspace in one place.</p>

              <div className="mt-8 space-y-4">
                {step === "email" && (
                  <>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Email address</label>
                      <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="mt-1 h-11"
                        onKeyDown={(e) => e.key === "Enter" && continueEmail()} data-testid="account-email" autoFocus />
                    </div>
                    <Button onClick={continueEmail} disabled={loading} className="w-full h-11 rounded-full gap-2" data-testid="account-continue">
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                      Continue
                      {!loading && <ArrowRight className="w-4 h-4" />}
                    </Button>
                    <p className="text-xs text-slate-500 text-center">We'll email you a one-time code — no password needed.</p>
                  </>
                )}

                {step === "details" && (
                  <>
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <button onClick={() => setStep("email")} className="hover:text-slate-800 flex items-center gap-1" data-testid="account-back">
                        <ArrowLeft className="w-4 h-4" /> Back
                      </button>
                      <span>· Creating an account for <span className="font-semibold text-slate-900">{email}</span></span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-1.5">What are you looking for?</p>
                      <div className="flex flex-wrap gap-2" data-testid="account-space-types">
                        {SPACE_TYPES.map((st) => {
                          const active = spaceType === st;
                          return (
                            <button type="button" key={st} data-testid={`account-space-${st.toLowerCase().replace(/ /g, "-")}`}
                              onClick={() => setSpaceType(st)}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                              {st}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Full name*</label>
                      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="mt-1 h-11" data-testid="account-name" autoFocus />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm font-medium text-slate-700">Phone*</label>
                        <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98765 43210" className="mt-1 h-11" data-testid="account-phone" />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Company</label>
                        <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company (optional)" className="mt-1 h-11" data-testid="account-company" />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">City</label>
                      <select value={city} onChange={(e) => setCity(e.target.value)} data-testid="account-city"
                        className={`mt-1 flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${city ? "text-slate-900" : "text-slate-400"}`}>
                        <option value="" className="text-slate-900">Select city</option>
                        {CITIES.map((c) => <option key={c} value={c} className="text-slate-900">{c}</option>)}
                      </select>
                    </div>
                    <Button onClick={createAccount} disabled={loading} className="w-full h-11 rounded-full gap-2" data-testid="account-create">
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                      Create my account
                      {!loading && <ArrowRight className="w-4 h-4" />}
                    </Button>
                  </>
                )}

                {step === "otp" && (
                  <>
                    <p className="text-sm text-slate-600">Enter the 6-digit code we sent to <span className="font-semibold text-slate-900">{email}</span>.</p>
                    <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="••••••" inputMode="numeric" className="h-12 text-center text-2xl tracking-[0.5em] font-bold"
                      onKeyDown={(e) => e.key === "Enter" && verify()} data-testid="account-otp" autoFocus />
                    <Button onClick={verify} disabled={loading} className="w-full h-11 rounded-full gap-2" data-testid="account-verify">
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Verify & continue
                    </Button>
                    <div className="flex justify-between text-sm">
                      <button onClick={() => setStep("email")} className="text-slate-500 hover:text-slate-800">Change email</button>
                      <button onClick={resend} className="text-primary font-semibold hover:underline" data-testid="account-resend">Resend code</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div className="bg-[#0f172a] text-white px-6 py-10 sm:py-12">
            <div className="max-w-3xl mx-auto lg:mx-0 lg:pl-[calc((100%-28rem)/2)]">
              <p className="font-display text-lg font-bold mb-6">Why create an account?</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {BENEFITS.map((b) => (
                  <div key={b.title}>
                    <span className="w-10 h-10 rounded-xl bg-white/10 text-sky-300 flex items-center justify-center mb-3"><b.icon className="w-5 h-5" /></span>
                    <p className="font-semibold text-sm">{b.title}</p>
                    <p className="text-sm text-slate-400 mt-1 leading-relaxed">{b.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right image */}
        <div className="hidden lg:block relative bg-slate-100">
          <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1200&q=70"
            alt="Member working at a CoWorkZen workspace" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a]/40 to-transparent" />
        </div>
      </main>
      <Footer />
    </div>
  );
}
